import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import Checkboxbar from '@intelligence/components/GenreSelect/components/Checkboxbar';
import { getLocaleText } from '@intelligence/components/GenreSelect/utils';

import type { CheckChangeContext } from 'tea-component';
import type { IBaseProps, IGenreCheckboxOptionWithChildren } from '@intelligence/components/GenreSelect/types';

import '@intelligence/components/GenreSelect/components/Checkboxbars/index.scss';

export interface ICheckboxbarsProps extends IBaseProps {
  level: number;
  checkboxListClassName?: string;
  // 滚动容器 ref，供父级面板做激活项滚动定位（可选，不影响现有调用方）
  containerRef?: React.RefObject<HTMLDivElement>;
  showRightArrow?: boolean | ((genre: IGenreCheckboxOptionWithChildren) => boolean);
  active?: boolean | ((genreValue: string) => boolean);
  indeterminate?: boolean | ((genre: IGenreCheckboxOptionWithChildren) => boolean);
  genres?: IGenreCheckboxOptionWithChildren[];
  onChange?: (genre: string, checked: boolean, context?: CheckChangeContext) => void;
  /**
   * 主、副品类复用一个onClick，所以这里参数是需要兼容主品类点击、副品类点击 两种场景
   * 1、点击主品类 需要更新 当前查看的主品类的索引，用于展示副品类信息（需要绑定一个主品类）
   * 2、点击副品类 需要执行副品类勾选逻辑，不需要index索引
   * @param index 当前点击品类的索引
   * @param genre 品类value（主、副）
   * @param checked 是否勾选（最新的值）
   */
  onClick?: (index: number, genre: string, checked: boolean) => void;
}

const Checkboxbars: React.FC<ICheckboxbarsProps> = (props) => {
  const { style, className, level, checkboxListClassName, containerRef, showRightArrow, active, indeterminate, genres, onChange, onClick } =
    props;

  const { locale, formatMessage } = useIntl();

  const isNoData = !Array.isArray(genres) || !genres?.length;

  return (
    <>
      {isNoData && (
        <span style={style} className={classnames('genre-no-data', className)}>
          {formatMessage({ id: `dbi_no_results_found` })}
        </span>
      )}
      {isNoData || (
        <div ref={containerRef} style={style} className={classnames('panel-radio-bars-container', className, checkboxListClassName)}>
          <ul className="panel-radio-bars">
            {genres.map((genre, genreIndex) => {
              const genreValue = genre?.value;
              const genreName = getLocaleText(locale, genre);
              const isActive = typeof active === 'function' ? active(genreValue) : !!active;
              const isIndeterminate = typeof indeterminate === 'function' ? indeterminate(genre) : !!indeterminate;
              const shouldShowRightArrow =
                typeof showRightArrow === 'function' ? showRightArrow(genre) : !!showRightArrow;
              return (
                <li key={`${level}__${genreIndex}__${genreValue}`} className="panel-radio-bar">
                  <Checkboxbar
                    showRightArrow={shouldShowRightArrow}
                    disabled={genre?.disabled}
                    disabledTips={genre?.disabledTips}
                    disabledTipsCN={genre?.disabledTipsCN}
                    active={isActive}
                    label={genreName}
                    value={genre?.checked}
                    indeterminate={isIndeterminate}
                    onChange={(payload, context) => {
                      onChange?.(genreValue, payload, context);
                    }}
                    onClick={() => {
                      const genreChecked = !genre?.checked;
                      onClick?.(genreIndex, genreValue, genreChecked);
                    }}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
};

export default Checkboxbars;
